import { Box, Button, Container } from "@mui/material";
import { QuoteSplash, QuoteSplashSubtitle, QuoteSplashTitle } from "layout/QuoteSplash"
import React, { useState, useEffect, ReactNode } from "react"
import { Canvas } from "renderer/ExhibitRenderer";
import { ScavengerHuntMapDelegate } from "./ScavengerHuntMapWidget";

import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';

enum Colors {
  Gold = '#faaf0b',
  GoldDark = '#c88c09',
  Eggshell = '#f2efdc',
  EggshellMedium = '#d9d1a3',
  EggshellDark = '#aea782',
}

// update these strings to match QR code url :exhibit_id targets
// /scavenger_hunt_:exhibit_id

enum ScavengerHuntQRCodes {
  zero = "04_15_acdqjpqlfm",
  one = "04_15_gqmdqyowzt",
  two = "04_15_xewfiqelod",
  reset = "reset",
  validate = "validate",
  map = "map"
}

type ScavengerHuntItem = {
  content: string,
  title: string,
  answer: string,
  link: string,
  image: string
};

type ScavengerHuntMap = {
  [key in ScavengerHuntQRCodes]: ScavengerHuntItem
}

const scavengerHuntSequence: ScavengerHuntMap = {
  [ScavengerHuntQRCodes.zero]: {
    content: "In the survival horror game, Resident Evil 4, what is the name of the burlap-masked enemy who wields a chainsaw?",
    title: "Challenge One",
    answer: "dr. salvador",
    link: "",
    image: ""
  },
  [ScavengerHuntQRCodes.one]: {
    content: "What game series has you hunting ghosts with a camera?",
    title: "Clue Two",
    answer: "fatal frame",
    link: "",
    image: "",
  },
  [ScavengerHuntQRCodes.two]: {
    content: "The Last of Us franchise features infected enemies that cannot see the player and only react to sound. What are they called?",
    title: "Clickers",
    answer: "three",
    link: "",
    image: "",
  },
  [ScavengerHuntQRCodes.reset]: {
    content: "Resetting",
    title: "",
    answer: "",
    link: "",
    image: ""
  },
  [ScavengerHuntQRCodes.validate]: {
    content: "Scavenger hunt status:",
    title: "MADE Scavenger Hunt Validation",
    answer: "",
    link: "",
    image: ""
  },
  [ScavengerHuntQRCodes.map]: {
    content: "Scavenger Hunt Map",
    title: "MADE Scavenger Hunt Map",
    answer: "",
    link: "",
    image: ""
  },
};

const ScavengerHuntInvalidLanding = () => {
  return (
    <>
      Invalid URL
    </>
  )
}

const ScavengerHuntReset = () => { 

  return (
    <>
      <QuoteSplash smallImage="" bigImage="">
        <QuoteSplashTitle>Reset Exhibits</QuoteSplashTitle>
        <QuoteSplashSubtitle>
          All exhibit states reset.
        </QuoteSplashSubtitle>
      </QuoteSplash>
    </>
  )
}


const leftItemStyles = {
  borderTopLeftRadius: "1rem",
  borderBottomLeftRadius: "1rem",
}
const rightItemStyles = {
  borderTopRightRadius: "1rem",
  borderBottomRightRadius: "1rem",
}
const centerItemStyles = {
}
const ProgressBar = ({completed}: {completed: Array<boolean>}) => {
  const total = completed.length;
  const completeCount = completed.filter((item) => item).length;
  const flexBasis = `calc(${100 / total}% - ${total - 1}rem/${total})`;
  const flex = `0 1 ${flexBasis}`;
  const progressItems = completed.map((_, index, items) => {
    const completedItem = completed[index];
    const itemStyles = index === 0 ? leftItemStyles : index === items.length - 1 ? rightItemStyles : centerItemStyles;
    const borderColor = completedItem ? Colors.GoldDark : Colors.EggshellDark;
    const backgroundColor = completedItem ? Colors.Gold : Colors.EggshellMedium;

    return <div key={index} style={ { flex, border: `0.1rem solid ${borderColor}`, backgroundColor, ...itemStyles} }></div>
  });
  return (
    <>
      <div style={ { display: "fixed", width: "100%", height: "100%"}}>
        <div style={ { width: "100%", height: "1rem", display: "flex", flexDirection: "row", flexWrap: "nowrap", justifyContent: "space-between"} }>
          {progressItems}
        </div>
      </div>
      <p style={{color: "black"}}>{completeCount} / {total} Complete! {completeCount < total ? "Keep going!" : "Good job!"}</p>
    </>
  )
}

/**
* MapChrome
* A react component that wraps the map canvas and renders a progress bar given an array of booleans indicating the completed puzzles
* @param completed - an array of booleans indicating the completed puzzles
*/
const MapChrome: React.FC<{children: JSX.Element, completed: Array<boolean>}> = ({children, completed}) => {
  return (
    <>
      <div style={ { display: "fixed", width: "100%", height: "100%", margin: "0", backgroundColor: Colors.Eggshell, userSelect: 'none', touchAction: 'none'}}>
        <div style={{padding: "1rem"}}>
          <h2 style={{color: "black"}}>Puzzle Completion</h2>
          <ProgressBar completed={completed} />

        </div>
        { children }
      </div>
    </>
  )
}

const ScavengerHuntVisualMap = ({seenMap, completed}: {seenMap: boolean, completed: Array<boolean>}) => {
  const mapDelegate:ScavengerHuntMapDelegate = new ScavengerHuntMapDelegate();

  let completedElement = (seenMap ? 
    <></> : 
    <ScavengerHuntPopover button={
      {
        label: "Open Map",
        callback: () => {
          localStorage.setItem(ScavengerHuntQRCodes.map, "completed");
          window.location.assign("/scavenger_hunt_map");
        }
      } 
    }>
      <h3>Welcome to the Scavenger Hunt!</h3>
      <p>Follow the map to find and solve three challenges to win a prize!</p>
    </ScavengerHuntPopover> 
  );

  return (
    <>
      <MapChrome completed={completed}>
        <Canvas renderer={ mapDelegate } ></Canvas>
      </MapChrome>
      { completedElement }
    </>
  )
}

const ScavengerChallengeCompleted = ({completed}: {completed: Number }) => {
  return (
    <>
      <QuoteSplash smallImage="" bigImage="">
        <QuoteSplashTitle>
          Challenge { completed.toString() } Completed!
        </QuoteSplashTitle>
        <QuoteSplashSubtitle>
          Challenge complete! <a href="/scavenger_hunt_map">Back to the Map</a>
        </QuoteSplashSubtitle>
      </QuoteSplash>
    </>
  )
}

const ScavengerHuntCompleted = () => {
  return (
    <>
      <QuoteSplash smallImage="" bigImage="">
        <QuoteSplashTitle>
          Everything Completed!
        </QuoteSplashTitle>
        <QuoteSplashSubtitle>
          All completed!
        </QuoteSplashSubtitle>
      </QuoteSplash>
    </>
  )
}

const ScavengerHuntPopover: React.FC<{button: {label: string, callback: (() => void)}, children:ReactNode}> = ({ button, children }) => {
  return (
    <Popover
      open
      anchorReference={ "none" }
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }} 
    >
      <Typography sx={{ p: "20px" }}>{ children }</Typography>
      <Button sx={{ margin: "20px" }} variant="contained" onClick={ button.callback }>{ button.label }</Button>
    </Popover>
  );
}

const ScavengerHuntQuizContent = ({ qrCode, huntItem, completed, }: {
  qrCode: string, 
  huntItem: ScavengerHuntItem, 
  completed: Array<boolean> }) => {

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const solved_text = "Challenge completed!";
  
  let bodyEl = document.getElementsByName("body")[0];
  let completedElement = ( 
    qrCode === ScavengerHuntQRCodes.zero && completed[0] ||
    qrCode === ScavengerHuntQRCodes.one && completed[1] ||
    qrCode === ScavengerHuntQRCodes.two && completed[2] ? 
    <ScavengerHuntPopover button={ 
      {
        label: "Open Map",
        callback: () => { window.location.assign("/scavenger_hunt_map"); } 
      }
    }>
      { solved_text }
    </ScavengerHuntPopover> : <></>
  );

  const handleClose = () => {
    setAnchorEl(null);
  };

  const submit = () => {
    if (qrCode == ScavengerHuntQRCodes.zero) {
      localStorage.setItem(ScavengerHuntQRCodes.zero, "completed");
    }
    if (qrCode == ScavengerHuntQRCodes.one) {
      localStorage.setItem(ScavengerHuntQRCodes.one, "completed");
    }
    if (qrCode == ScavengerHuntQRCodes.two) {
      localStorage.setItem(ScavengerHuntQRCodes.two, "completed");
    }
    window.location.reload();
  }

  return (
    <>
      <QuoteSplash smallImage="" bigImage="">
        <QuoteSplashTitle>{ huntItem.title }</QuoteSplashTitle>
        <QuoteSplashSubtitle>
          <p>
            { huntItem.content }
          </p>
          <p>
            <input type="text" placeholder="Answer" />
          </p>
          <Button variant="contained" onClick={ submit }>Submit</Button>
        </QuoteSplashSubtitle>
      </QuoteSplash>
      { completedElement }
    </>
  );
}

const ScavengerHuntValidate = ({ huntItem, hasFinished }: { huntItem: ScavengerHuntItem, hasFinished: boolean }) => {
  return (
    <>
      <QuoteSplash smallImage="" bigImage="">
        <QuoteSplashTitle>{ huntItem.title }</QuoteSplashTitle>
        <QuoteSplashSubtitle>
          { huntItem.content }
          { hasFinished? <p>Scavenger hunt completed!</p> : <p>Scavenger hunt incomplete!</p> }
        </QuoteSplashSubtitle>
      </QuoteSplash>
    </>
  );
}

const ScavengerHuntItem = ({ exhibit_id }: { exhibit_id: string }) => {
  const huntItem = scavengerHuntSequence[exhibit_id];
  const [seenMap, setSeenMap] = useState(false);
  const [completed, setCompleted] = useState([false, false, false]);

  useEffect(() => {
    const hunt_0 = localStorage.getItem(ScavengerHuntQRCodes.zero) !== null;
    const hunt_1 = localStorage.getItem(ScavengerHuntQRCodes.one) !== null;
    const hunt_2 = localStorage.getItem(ScavengerHuntQRCodes.two) !== null;
    const map = localStorage.getItem(ScavengerHuntQRCodes.map) !== null;

    setSeenMap(map);
    setCompleted([hunt_0, hunt_1, hunt_2]);
    
    return () => { };
  }, []);


  if (
    exhibit_id === ScavengerHuntQRCodes.map
  ) {
    return ScavengerHuntVisualMap({ seenMap: seenMap, completed: completed });
  }

  if (exhibit_id === ScavengerHuntQRCodes.reset) {
    localStorage.removeItem(ScavengerHuntQRCodes.zero);
    localStorage.removeItem(ScavengerHuntQRCodes.one);
    localStorage.removeItem(ScavengerHuntQRCodes.two);
    localStorage.removeItem(ScavengerHuntQRCodes.map);
    return ScavengerHuntReset();
  }

  return ScavengerHuntQuizContent({ qrCode: exhibit_id, huntItem, completed: completed });
}

export const ScavengerHuntWidget = ({ exhibit_id }) => {
  if (!exhibit_id) {
    exhibit_id = ScavengerHuntQRCodes.map;
  };

  if (!scavengerHuntSequence[exhibit_id]) {
    console.error('unknown scavenger hunt exhibit_id');
    return <ScavengerHuntInvalidLanding />;
  }

  return <ScavengerHuntItem exhibit_id={exhibit_id} />;
}
